import { useId, useRef } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/lib/components/Select";
import { Button } from "@/lib/components/Button/Button";

const INTENSITY_OPTIONS = [
  { label: "Subtle", value: 0.4 },
  { label: "Medium", value: 0.8 },
  { label: "Strong", value: 1 },
];

const SHAPE_OPTIONS = [
  { label: "Shape 1", value: 1 },
  { label: "Shape 2", value: 2 },
  { label: "Shape 3", value: 3 },
  { label: "Shape 4", value: 4 },
  { label: "Shape 5", value: 5 },
];

export function TransitionIntensityControl({
  value,
  onChange,
  onPreview,
  onPreviewEnd,
}: {
  value: number;
  onChange: (value: number) => void;
  onPreview: (value: number) => void;
  onPreviewEnd: () => void;
}) {
  // Snap values saved before the presets existed to the closest one.
  const selected = INTENSITY_OPTIONS.reduce((closest, option) =>
    Math.abs(option.value - value) < Math.abs(closest.value - value)
      ? option
      : closest,
  );

  return (
    <div className="flex flex-col">
      <div className="input-label">Intensity</div>
      <div className="flex gap-2">
        {INTENSITY_OPTIONS.map((option) => (
          <Button
            key={option.value}
            variant={
              option.value === selected.value ? "gradientBorder" : "outline"
            }
            size="sm"
            className="font-medium"
            onClick={() => onChange(option.value)}
            onMouseEnter={() => onPreview(option.value)}
            onMouseLeave={onPreviewEnd}
          >
            {option.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

export function TransitionPatternControl({
  value,
  onChange,
  onPreview,
  onPreviewEnd,
}: {
  value: number;
  onChange: (value: number) => void;
  onPreview: (value: number) => void;
  onPreviewEnd: () => void;
}) {
  const id = useId();
  const selectedRef = useRef(false);

  return (
    <div className="flex flex-col">
      <label htmlFor={id} className="input-label">
        Shape
      </label>
      <Select
        value={
          SHAPE_OPTIONS.some((option) => option.value === value)
            ? String(value)
            : undefined
        }
        onValueChange={(next) => {
          selectedRef.current = true;
          onChange(Number(next));
        }}
        onOpenChange={(open) => {
          if (!open && !selectedRef.current) onPreviewEnd();
          selectedRef.current = false;
        }}
      >
        <SelectTrigger id={id} className="h-9 font-medium">
          <SelectValue placeholder="Custom" />
        </SelectTrigger>
        <SelectContent>
          {SHAPE_OPTIONS.map((option) => (
            <SelectItem
              key={option.value}
              value={String(option.value)}
              onPointerEnter={() => onPreview(option.value)}
              onPointerLeave={onPreviewEnd}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
